import IconButton from '@mui/material/IconButton';
import { SKINNED_HEADER_MENU_ALLOWED_ROUTES } from '../../config/sidebar';
import { memo, useMemo } from 'react';
import { Stack } from '@mui/material';
import { useLocation } from 'react-router';
import TranslateButton from '../atoms/TranslateButton';
import AuthButtons from './AuthButtons';
import AccountMenuRenderer from './menus/AccountMenuRenderer';
import HeaderTabNavigation from './HeaderTabNavigation';
import Logo from '../icons/Logo';
import SwipeableLogo from '../icons/SwipeableLogo';
import { StyleObj } from '../../@types';
import useBreakpoints from '../../hooks/useBreakpoints';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import MuiMenuIcon from '@mui/icons-material/Menu';
import Search from '../atoms/Search';
import { useSkinConfigs } from '../../hooks/useSkinConfigs';
import useProductAvailability from '../../hooks/useProductAvailability';
import { useRoles } from '../../hooks/useRoles';

import { mergeSx } from '../../utils/styles';

const styles: StyleObj = {
  menuIcon: {
    color: 'info.main',
  },
  mobileNavigationWrapper: {
    padding: '0px 30px',
    mt: '0px !important',
    '& > div > div > div': {
      display: 'flex',
      justifyContent: 'space-between',
    },
    '& > div > div > div > button': {
      fontSize: '15px !important',
      fontWeight: '600 !important',
      flex: 1,
    },
    '& div': {
      width: '100%',
    },
  },
};

const MobilePublicToolbar = memo(({ openDrawer, pathname }: { openDrawer: () => void; pathname: string }) => {
  return (
    <Stack
      spacing={2}
      direction='column'
      sx={{
        py: 0.5,
        width: 1,
      }}
    >
      <Stack
        direction='row'
        sx={{
          width: 1,
          alignItems: 'center',
        }}
      >
        <Stack
          sx={{
            flex: 1,
          }}
        >
          <MenuIcon openDrawer={openDrawer} pathname={pathname} />
        </Stack>
        <SwipeableLogo size='small' />
        <Stack
          sx={{
            flex: 1,
          }}
        >
          <AuthButtons />
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        direction='row'
        sx={mergeSx(
          {
            alignItems: 'center',
            minHeight: '50px',
          },
          styles.mobileNavigationWrapper
        )}
      >
        <HeaderTabNavigation />
      </Stack>
    </Stack>
  );
});

const MobileAuthToolbar = memo(({ openDrawer, pathname }: { openDrawer: () => void; pathname: string }) => {
  const isCasinoPage = pathname.startsWith('/casino');
  const { isPlayer } = useRoles();
  const { isCasinoOnlyProduct } = useProductAvailability();
  const displayLogo = isCasinoOnlyProduct && (!isPlayer || isCasinoPage);

  return (
    <>
      {displayLogo ? (
        <Logo size='small' isClickable={false} />
      ) : (
        <MenuIcon openDrawer={openDrawer} pathname={pathname} />
      )}
      <HeaderTabNavigation />
      <Stack
        direction='row'
        spacing={1}
        sx={{
          alignItems: 'center',
          ml: 'auto',
        }}
      >
        <AccountMenuRenderer />
      </Stack>
    </>
  );
});

const DesktopToolbar = memo(({ isAuthenticated }: { isAuthenticated: boolean }) => {
  return (
    <>
      <Logo />
      <HeaderTabNavigation />
      {isAuthenticated ? (
        <Stack
          direction='row'
          spacing={1}
          sx={{
            alignItems: 'center',
            ml: 'auto',
          }}
        >
          <Search />
          <AccountMenuRenderer />
        </Stack>
      ) : (
        <Stack
          direction='row'
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Search />
          <TranslateButton />
          <AuthButtons />
        </Stack>
      )}
    </>
  );
});

const MenuIcon = memo(({ openDrawer, pathname }: { openDrawer: () => void; pathname: string }) => {
  const headerMenuAllowedRoutes = useSkinConfigs(SKINNED_HEADER_MENU_ALLOWED_ROUTES);
  const showMenu = useMemo(
    () => headerMenuAllowedRoutes.some((route) => pathname.includes(route)),
    [pathname, headerMenuAllowedRoutes]
  );
  return (
    <>
      {showMenu && (
        <IconButton onClick={openDrawer} sx={{ justifyContent: 'flex-start' }}>
          <MuiMenuIcon sx={styles.menuIcon} />
        </IconButton>
      )}
    </>
  );
});

type Props = { openDrawer: () => void };

const ToolbarContent: React.FC<Props> = memo(({ openDrawer }) => {
  const { isMobile } = useBreakpoints();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;

  if (!isMobile) {
    return <DesktopToolbar isAuthenticated={isAuthenticated} />;
  }
  if (!isAuthenticated) {
    return <MobilePublicToolbar openDrawer={openDrawer} pathname={pathname} />;
  }

  return <MobileAuthToolbar openDrawer={openDrawer} pathname={pathname} />;
});

export default ToolbarContent;
